<template>
  <div
    :class="[
      'flex flex-col justify-center items-center px-6 pt-4 w-full h-full bg-white',
    ]"
  >
    <!-- Hero -->
    <div
      :style="{
        backgroundImage: `url(${contributor.profile_image_hero})`,
      }"
      class="flex flex-col h-auto w-full bg-white rounded-2xl overflow-hidden relative bg-cover bg-center h-[600px] w-full bg-cw-700 cw__hero-banner"
    >
      <div
        class="absolute bottom-16 flex flex-col gap-8 w-full px-12 lg:px-32 text-white z-10"
      >
        <Logo :path="contributor.logo" />
        <div class="flex items-end justify-between flex-wrap gap-8">
          <div class="flex flex-col gap-4">
            <h5 class="mb-0 tracking-wide font-normal">
              CareerWise Contributor
            </h5>
            <h2 class="text-5xl mb-0">{{ contributor.title }}</h2>
          </div>
          <div
            v-if="showContactDetails"
            class="flex rounded-md bg-primary text-white py-2"
          >
            <!-- Website -->
            <a
              v-if="website"
              :href="website"
              target="_blank"
              :title="website"
              :class="[
                'px-5 py-1 group',
                { 'border-r border-white': hasMultipleContactDetails },
              ]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6 group-hover:text-black"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                />
              </svg>
            </a>
            <!-- Website -->

            <!-- Email -->
            <a
              v-if="contactEmail"
              title="Copy contact email address"
              :class="[
                'px-5 py-1 group',
                { 'border-r border-white': !!contactPhone },
              ]"
              @click="copyToClipboard(contactEmail, 'Contact email address')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6 group-hover:text-black"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                />
              </svg>
            </a>
            <!-- Email -->

            <!-- Phone -->
            <a
              v-if="contactPhone"
              class="px-5 py-1 group"
              title="Copy contact phone number"
              @click="copyToClipboard(contactPhone, 'Contact phone number')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6 group-hover:text-black"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
                />
              </svg>
            </a>
            <!-- Phone -->
          </div>
        </div>
      </div>
    </div>
    <!-- /Hero -->

    <!-- About Us -->
    <div
      v-if="canShowAboutUs"
      class="flex flex-col gap-12 w-full px-6 md:px-12 lg:px-32 py-12 md:py-28"
    >
      <div class="flex flex-col md:flex-row md:gap-x-12 gap-y-12 w-full">
        <div class="flex flex-col w-full md:w-1/3">
          <div
            class="flex justify-center items-center w-full rounded-3xl bg-primary border-gray-50 border-8 p-6 text-white md:cw__aspect-ratio--1"
          >
            <h2 class="mb-0 text-5xl text-center">About Us</h2>
          </div>
        </div>

        <div class="flex flex-col w-full md:w-2/3">
          <div
            class="prose cw__profile-bio rounded-3xl border-primary border-8 !p-6 flex-grow overflow-y-scroll md:cw__flex-basis-0"
            v-html="contributor.profile_bio"
          />
        </div>
      </div>

      <div v-if="canShowMediaRow" class="grid grid-cols-4 gap-12 w-full">
        <div
          v-if="videoSrc"
          :class="[
            'col-span-4',
            { 'lg:col-span-3': contributor.profile_image_secondary },
          ]"
        >
          <iframe
            type="iframe"
            class="rounded-3xl w-full shadow-lg"
            :src="videoSrc"
            frameborder="0"
            height="480"
            allowfullscreen
          />
        </div>
        <div
          v-if="contributor.profile_image_secondary"
          :style="{
            backgroundImage: `url(${contributor.profile_image_secondary})`,
          }"
          :class="[
            'rounded-3xl bg-cover bg-center border-gray-50 border-8 p-6 flex justify-center items-center text-white min-h-[380px] col-span-4',
            {
              'lg:col-span-1': videoSrc,
            },
          ]"
        />
      </div>
    </div>
    <!-- /About Us -->
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useStore } from '../../../js/store/index.js'
import { useRouter } from 'vue-router/composables'
import { EventBus } from '../../../js/plugins/event-bus'
import Logo from './ContributorLogo.vue'

export default defineComponent({
  name: 'Profile',
  components: { Logo },

  setup() {
    const store = useStore()
    const router = useRouter()

    const contributor = computed(
      () => store.getters['contributors/getOrganisationProfile'],
    )
    const canShowAboutUs = computed(
      () => contributor.value.profile_bio || canShowMediaRow.value,
    )
    const canShowMediaRow = computed(
      () => videoSrc.value || contributor.value.profile_image_secondary,
    )

    const heroImagePath = ref('/img/expo-banner.png')
    const videoSrc = computed(() => {
      const code = contributor.value.video_code
      const type = contributor.value.video_type
      const TYPE_MAP = {
        YouTube: 'https://www.youtube.com/embed/',
        Vimeo: 'https://player.vimeo.com/video/',
      }

      if (code && type) {
        return `${TYPE_MAP[type]}${code}`
      }

      return null
    })
    const website = computed(() => contributor.value.website)
    const contactEmail = computed(() => contributor.value.public_email)
    const contactPhone = computed(() => contributor.value.public_phone)
    const contactDetailsArray = computed(() => [
      website.value,
      contactEmail.value,
      contactPhone.value,
    ])
    const showContactDetails = computed(() => {
      return !!contactDetailsArray.value.filter((detail) => detail).length
    })
    const hasMultipleContactDetails = computed(() => {
      return contactDetailsArray.value.filter((detail) => detail).length >= 2
    })

    const copyToClipboard = (value, key) => {
      navigator.clipboard.writeText(value)
      EventBus.$emit('triggerAlertResponse', {
        status: 'success',
        message: `${key} copied to clipboard`,
      })
    }

    const routeTo = (name) => {
      router.push({ name })
    }

    return {
      contributor,
      canShowAboutUs,
      canShowMediaRow,
      heroImagePath,
      videoSrc,
      website,
      contactEmail,
      contactPhone,
      showContactDetails,
      hasMultipleContactDetails,
      copyToClipboard,
      routeTo,
    }
  },
})
</script>

<style scoped>
.cw__hero-banner::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: rgb(0, 0, 0);
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.1) 40%,
    rgba(0, 0, 0, 0.5) 75%,
    rgba(0, 0, 0, 0.8) 100%
  );
}

/* md and up */
@media (min-width: 768px) {
  .md\:cw__aspect-ratio--1 {
    aspect-ratio: 1;
  }

  .md\:cw__flex-basis-0 {
    flex-basis: 0px;
  }
}
</style>
